import { emitSpan } from "../../lib/tracing";

// Handle from addQuery, scoped to that query's id so a caller can't touch
// another's. resolve marks it settled; remove drops it (torn down first).
export type TracedQuery = {
  resolve: () => void;
  remove: () => void;
};

export type Tracer = {
  addQuery: () => TracedQuery;
  start: (path: string) => void;
  isSettling: () => boolean;
  markCold: () => void;
  // performance.now() the current navigation started, or undefined if none is
  // in flight. Lets a query report its offset into the navigation.
  startedAt: () => number | undefined;
};

/**
 * Time to data-settle per navigation. Every useQuery mounted during a
 * navigation is traced — addQuery() on mount, the handle's resolve() on settle
 * — and the navigation emits, timed to the last resolve, once the pending set
 * drains and stays empty across a two-frame grace. The grace lets a cascade (a
 * query that only mounts after its parent resolves) reopen the window before we
 * emit. The initial hard load is the first navigation; a route with no queries
 * settles on the grace alone.
 */
export function createTracer(): Tracer {
  let type: "initial" | "soft" = "initial";
  let path = "";
  let start = 0;
  let cold = false;
  let emitted = true; // no navigation in flight until start()
  let durationMs = 0;
  let started = false;
  let nextId = 0;
  let queryCount = 0;
  const pending = new Set<number>();
  let settleFrame: number | undefined;

  // Any addQuery() before the grace elapses cancels this, reopening the window.
  function scheduleSettle() {
    cancelSettle();
    settleFrame = requestAnimationFrame(() => {
      settleFrame = requestAnimationFrame(() => {
        settleFrame = undefined;
        if (emitted || pending.size > 0) return;
        // Empty route: nothing resolved, so this grace frame is the settle.
        if (durationMs === 0) durationMs = performance.now() - start;
        emitNavigationSpan();
      });
    });
  }

  function cancelSettle() {
    if (settleFrame !== undefined) {
      cancelAnimationFrame(settleFrame);
      settleFrame = undefined;
    }
  }

  function emitNavigationSpan() {
    emitted = true;
    cancelSettle();
    if (import.meta.env.DEV) {
      const queries = `${queryCount} ${queryCount === 1 ? "query" : "queries"}`;
      const detail =
        queryCount > 0 ? `${queries}, ${cold ? "cold" : "warm"}` : queries;
      const hard = type === "initial" ? " hard" : "";
      console.log(
        `[navigation] ${path} ${Math.round(durationMs)}ms${hard} (${detail})`
      );
    }
    emitSpan(
      "ui.navigation",
      {
        "navigation.type": type,
        "navigation.path": path,
        "navigation.duration_ms": Math.round(durationMs),
        "navigation.cold": cold ? 1 : 0,
        "navigation.query_count": queryCount,
      },
      durationMs
    );
  }

  return {
    // Call from a layout effect: the layout phase finishes before any passive
    // effect, so this lands before queries addQuery() in their mount effects.
    start(next: string) {
      type = started ? "soft" : "initial";
      started = true;
      path = next;
      // performance.now() is relative to timeOrigin, so anchoring the initial
      // navigation at 0 captures the pre-app load; a soft one clocks from here.
      start = type === "initial" ? 0 : performance.now();
      cold = false;
      emitted = false;
      durationMs = 0;
      queryCount = 0;
      pending.clear();
      scheduleSettle();
    },
    addQuery() {
      const id = nextId++;
      if (!emitted) {
        cancelSettle();
        queryCount++;
        pending.add(id);
      }
      return {
        resolve() {
          if (emitted || !pending.delete(id)) return;
          durationMs = performance.now() - start;
          if (pending.size === 0) scheduleSettle();
        },
        remove() {
          if (emitted || !pending.delete(id)) return;
          if (pending.size === 0) scheduleSettle();
        },
      };
    },
    isSettling() {
      return !emitted;
    },
    // Cold = an uncached query settled during the navigation.
    markCold() {
      if (!emitted) cold = true;
    },
    startedAt() {
      return emitted ? undefined : start;
    },
  };
}
